import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface IntegrationSyncRow {
  key: string;
  label: string;
  icon: React.ElementType;
  count?: number;
  href?: string;
  loading?: boolean;
  value?: string | null;
}

function DataSyncRow({
  row,
  loading,
  onNavigate,
  isLast,
  linkTitle,
}: {
  row: IntegrationSyncRow;
  loading: boolean;
  onNavigate: (href: string) => void;
  isLast: boolean;
  linkTitle?: string;
}) {
  return (
    <div
      className={cn(
        'grid grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] items-center',
        !isLast && 'border-b border-[#EDF1F7]',
      )}
    >
      <div className="flex items-center gap-2 border-r border-[#EDF1F7] px-4 py-3 text-[13px] font-medium text-[#6A7282]">
        <row.icon className="h-4 w-4 shrink-0 text-[#98A2B3]" aria-hidden />
        {row.label}
      </div>
      <div className="px-4 py-3">
        {row.href === undefined ? (
          <span className="text-[13px] font-semibold text-[#101828]">{row.value || '—'}</span>
        ) : loading || row.loading ? (
          <Loader2
            className="h-3.5 w-3.5 animate-spin text-[#98A2B3]"
            role="status"
            aria-label={`${row.label} still syncing`}
          />
        ) : (
          <button
            type="button"
            onClick={() => onNavigate(row.href as string)}
            className="rounded text-[13px] font-semibold text-[#101828] transition-colors hover:text-[#0EB64F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0EB64F]/40"
            title={linkTitle}
          >
            {row.count ?? 0}
          </button>
        )}
      </div>
    </div>
  );
}

export function IntegrationDataSync({
  rows,
  loading = false,
  onNavigate,
  title = 'Data sync',
  linkTitle,
}: {
  rows: IntegrationSyncRow[];
  loading?: boolean;
  onNavigate: (href: string) => void;
  title?: string | null;
  linkTitle?: string;
}) {
  if (rows.length === 0) return null;
  return (
    <section>
      {title ? <h3 className="mb-2 text-[14px] font-bold text-[#101828]">{title}</h3> : null}
      <div className="overflow-hidden rounded-[10px] border border-[#EDF1F7] bg-white">
        {rows.map((row, i) => (
          <DataSyncRow
            key={row.key}
            row={row}
            loading={loading}
            isLast={i === rows.length - 1}
            onNavigate={onNavigate}
            linkTitle={linkTitle}
          />
        ))}
      </div>
    </section>
  );
}

export default IntegrationDataSync;
